/**
 * Canonical helpers for the internal dev-mode filter note appended to mrge-io
 * review comments. Keep the marker strings, bullet formatters, parser, and
 * remover together so GitHub comments, AI prompt extraction, and admin tooling
 * cannot drift into incompatible formats.
 */
export const DEV_MODE_FILTER_NOTE_START = '<!-- internal: dev-mode-filter-note -->';
export const DEV_MODE_FILTER_NOTE_END = '<!-- /internal: dev-mode-filter-note -->';
export const DEV_MODE_FILTER_NOTE_HEADER =
  '🧪 Internal: Dev mode kept this violation, but filters would have removed it.';

const SCREENING_FILTER_LINE_PREFIX = '- Screening filters: failed ';
const DEEP_FILTER_LINE_PREFIX = '- Deepfilter: ';
const LEGACY_GPT5_DEEP_FILTER_LINE_PREFIX = '- GPT-5 deep filter: ';
const DEEP_FILTER_LINE_PREFIXES = [DEEP_FILTER_LINE_PREFIX, LEGACY_GPT5_DEEP_FILTER_LINE_PREFIX];
const LEGACY_DEEP_FILTER_MARKERS = [
  'DEV MODE: This violation would have been filtered out by Deepfilter',
  'DEV MODE: This violation would have been filtered out by GPT-5',
];
const LEGACY_DEEP_FILTER_REASON_REGEX = /(?:\*\*)?(?:Deepfilter|GPT-5)(?:\*\*)?:/;

const DEV_MODE_FILTER_NOTE_BLOCK_REGEX =
  /<!-- internal: dev-mode-filter-note -->\n?([\s\S]*?)<!-- \/internal: dev-mode-filter-note -->/g;

export type DevModeFilteringReason = 'screening_filters' | 'gpt5_deep_filter';

/**
 * Parsed shape for both the new wrapped note and legacy DEV MODE paragraphs.
 *
 * `filteringReasons` preserves every filter stage that would have removed the
 * violation. `filteringReason` remains for older UI code that expects one value.
 */
export type ParsedDevModeFilterNote = {
  wouldBeFilteredOut: true;
  filteringReasons: DevModeFilteringReason[];
  filteringReason?: DevModeFilteringReason | 'multiple';
  failingFilters?: string[];
  gpt5Reasoning?: string;
};

function normalizeNoteLine(value: string): string {
  // Note: GitHub comment bullets are line-based, so collapse model-provided
  // newlines before writing reasoning into a single parseable bullet.
  return value.replace(/\s+/g, ' ').trim();
}

function withoutTrailingPeriod(value: string): string {
  return value.replace(/\.$/, '');
}

function splitFilterNames(value: string): string[] {
  return withoutTrailingPeriod(value)
    .split(',')
    .map((filterName) => filterName.trim())
    .filter(Boolean);
}

function summarizeFilteringReason(
  reasons: DevModeFilteringReason[],
): DevModeFilteringReason | 'multiple' | undefined {
  if (reasons.length === 1 && reasons[0]) {
    return reasons[0];
  }

  if (reasons.length === 0) {
    return undefined;
  }

  // Legacy consumers should not accidentally prefer Deepfilter over screening when
  // both stages filtered the same violation.
  return 'multiple';
}

function getDeepFilterLinePrefix(line: string): string | undefined {
  return DEEP_FILTER_LINE_PREFIXES.find((prefix) => line.startsWith(prefix));
}

function getLegacyDeepFilterAnnotationStart(content: string): number {
  const markerIndexes = LEGACY_DEEP_FILTER_MARKERS.map((marker) => content.indexOf(marker)).filter(
    (index) => index !== -1,
  );

  if (markerIndexes.length === 0) {
    return -1;
  }

  return Math.min(...markerIndexes);
}

function extractLegacyDeepFilterReasoning(content: string): string | null {
  const deepFilterAnnotationStart = getLegacyDeepFilterAnnotationStart(content);
  if (deepFilterAnnotationStart === -1) {
    return null;
  }

  const deepFilterAnnotation = content.slice(deepFilterAnnotationStart);
  const reasonMatch = deepFilterAnnotation.match(LEGACY_DEEP_FILTER_REASON_REGEX);
  if (reasonMatch?.index === undefined) {
    return null;
  }

  const reasoningStart = reasonMatch.index + reasonMatch[0].length;
  const reasoningEnd = deepFilterAnnotation.indexOf('\n', reasoningStart);
  const reasoning = deepFilterAnnotation
    .slice(reasoningStart, reasoningEnd === -1 ? undefined : reasoningEnd)
    .trim();

  return reasoning || null;
}

function isLegacyScreeningDevModeLine(line: string): boolean {
  let trimmedLine = line.trim();
  if (trimmedLine.startsWith('*')) {
    trimmedLine = trimmedLine.slice(1);
  }
  if (trimmedLine.endsWith('*')) {
    trimmedLine = trimmedLine.slice(0, -1);
  }

  return (
    trimmedLine === 'DEV MODE: This violation would have been filtered out.' ||
    trimmedLine.startsWith(
      'DEV MODE: This violation would have been filtered out by screening filters. Failing filters:',
    )
  );
}

function extractDevModeFilterNoteLines(content: string): string[] {
  const match = [...content.matchAll(DEV_MODE_FILTER_NOTE_BLOCK_REGEX)][0];
  if (!match?.[1]) {
    return [];
  }

  return match[1]
    .trim()
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line.startsWith('- '));
}

function parseWrappedDevModeFilterNote(content: string): ParsedDevModeFilterNote | null {
  const lines = extractDevModeFilterNoteLines(content);
  if (lines.length === 0) {
    return null;
  }

  const result: ParsedDevModeFilterNote = {
    wouldBeFilteredOut: true,
    filteringReasons: [],
  };

  const screeningLine = lines.find((line) => line.startsWith(SCREENING_FILTER_LINE_PREFIX));
  if (screeningLine) {
    const failingFilters = screeningLine.slice(SCREENING_FILTER_LINE_PREFIX.length);
    result.failingFilters = splitFilterNames(failingFilters);
    result.filteringReasons.push('screening_filters');
  }

  const deepFilterLine = lines.find((line) => getDeepFilterLinePrefix(line));
  const deepFilterPrefix = deepFilterLine ? getDeepFilterLinePrefix(deepFilterLine) : undefined;
  if (deepFilterLine && deepFilterPrefix) {
    result.gpt5Reasoning = deepFilterLine.slice(deepFilterPrefix.length).trim();
    result.filteringReasons.push('gpt5_deep_filter');
  }

  result.filteringReason = summarizeFilteringReason(result.filteringReasons);

  return result;
}

function parseLegacyDevModeFilterNote(content: string): ParsedDevModeFilterNote | null {
  // Legacy comments are still present in old reviews and may be re-parsed by
  // admin tools or reply flows, so removal and parsing must keep supporting them.
  const devModeMatch = content.match(/\*?DEV MODE:(.*)(?:\n|$)/);
  if (!devModeMatch) {
    return null;
  }

  const result: ParsedDevModeFilterNote = {
    wouldBeFilteredOut: true,
    filteringReasons: [],
  };

  const screeningMatch = devModeMatch[1]?.match(/Failing filters:[ \t]*([^\s.][^.]*)(?:\.|$)/);
  if (screeningMatch?.[1]) {
    result.failingFilters = splitFilterNames(screeningMatch[1]);
    result.filteringReasons.push('screening_filters');
  }

  const legacyDeepFilterReasoning = extractLegacyDeepFilterReasoning(content);
  if (legacyDeepFilterReasoning) {
    result.gpt5Reasoning = legacyDeepFilterReasoning;
    result.filteringReasons.push('gpt5_deep_filter');
  }

  result.filteringReason = summarizeFilteringReason(result.filteringReasons);

  return result;
}

function removeLegacyDevModeAnnotations(content: string): string {
  // Remove screening filter DEV MODE annotation.
  let result = content
    .split('\n')
    .filter((line) => !isLegacyScreeningDevModeLine(line))
    .join('\n');

  // Remove deep filter DEV MODE annotation. This goes to the end of the
  // content since the legacy reasoning can be multi-line.
  const deepFilterIndex = getLegacyDeepFilterAnnotationStart(result);
  if (deepFilterIndex !== -1) {
    result = result.substring(0, deepFilterIndex).trim();
  }

  return result.trim();
}

/**
 * Formats the screening-filter bullet used inside the wrapped internal note.
 */
export function formatScreeningDevModeFilterNoteLine(failingFilters: string[]): string {
  const filters =
    failingFilters.length > 0
      ? failingFilters.join(', ')
      : 'None explicitly failed (error or default)';
  return `${SCREENING_FILTER_LINE_PREFIX}${filters}.`;
}

/**
 * Formats the deep-filter bullet used inside the wrapped internal note.
 *
 * Reasoning is normalized to one line so the parser can treat each bullet as an independent field.
 */
export function formatDeepDevModeFilterNoteLine(reason: string): string {
  return `${DEEP_FILTER_LINE_PREFIX}${normalizeNoteLine(reason)}`;
}

/**
 * Formats a concise human-readable label for trace summaries and copy payloads.
 */
export function formatDevModeFilterSummary(parsed: ParsedDevModeFilterNote): string {
  const labels: string[] = [];

  if (parsed.failingFilters?.length) {
    labels.push(parsed.failingFilters.join(', '));
  }

  if (parsed.gpt5Reasoning) {
    labels.push('Deepfilter');
  }

  if (labels.length > 0) {
    return labels.join('; ');
  }

  return parsed.filteringReason ?? 'Filters';
}

/**
 * Formats the wrapped internal dev-mode filter note shown on mrge-io review comments.
 *
 * The HTML comments are hidden in rendered Markdown but survive raw GitHub API
 * reads, which gives sanitizers a stable boundary around the visible internal note.
 */
export function formatDevModeFilterNoteBlock(bulletLines: string[]): string {
  return [
    DEV_MODE_FILTER_NOTE_START,
    DEV_MODE_FILTER_NOTE_HEADER,
    ...bulletLines.map(normalizeNoteLine),
    DEV_MODE_FILTER_NOTE_END,
  ].join('\n');
}

/**
 * Appends or merges a bullet line into the internal dev-mode filter note block.
 *
 * Existing note blocks are removed and rebuilt so each comment has at most one
 * block even when screening and deep filtering annotate the same violation.
 */
export function appendDevModeFilterNoteLine(content: string, line: string): string {
  const normalizedLine = normalizeNoteLine(line);
  const existingLines = extractDevModeFilterNoteLines(content);
  const bodyWithoutNote = removeDevModeFilterNote(content);

  const deepFilterLinePrefix = getDeepFilterLinePrefix(normalizedLine);
  const updatedLines = normalizedLine.startsWith(SCREENING_FILTER_LINE_PREFIX)
    ? [
        ...existingLines.filter(
          (existingLine) => !existingLine.startsWith(SCREENING_FILTER_LINE_PREFIX),
        ),
        normalizedLine,
      ]
    : deepFilterLinePrefix
      ? [
          ...existingLines.filter((existingLine) => !getDeepFilterLinePrefix(existingLine)),
          normalizedLine,
        ]
      : existingLines.includes(normalizedLine)
        ? existingLines
        : [...existingLines, normalizedLine];

  const block = formatDevModeFilterNoteBlock(updatedLines);

  if (!bodyWithoutNote.trim()) {
    return block;
  }

  return `${bodyWithoutNote.trim()}\n\n${block}`;
}

/**
 * Removes the wrapped internal dev-mode filter note and legacy dev-mode annotations.
 *
 * Call this before generic HTML-comment stripping. Otherwise only the hidden
 * markers are removed and the visible emoji/bullets remain in the comment body.
 */
export function removeDevModeFilterNote(content: string): string {
  let result = content.replace(DEV_MODE_FILTER_NOTE_BLOCK_REGEX, '');
  result = removeLegacyDevModeAnnotations(result);
  return result.replace(/\n{3,}/g, '\n\n').trim();
}

export function splitDevModeFilterNote(content: string): { body: string; note: string } {
  const notes = content.match(DEV_MODE_FILTER_NOTE_BLOCK_REGEX);
  if (!notes) {
    return { body: content, note: '' };
  }
  return { body: removeDevModeFilterNote(content), note: notes.join('\n\n') };
}

/**
 * Parses dev-mode filter details from either the wrapped note or the legacy
 * paragraphs used before this format existed.
 */
export function parseDevModeFilterNote(content: string): ParsedDevModeFilterNote | null {
  return parseWrappedDevModeFilterNote(content) ?? parseLegacyDevModeFilterNote(content);
}
